import styled, { css, keyframes } from 'styled-components';
import {
  SPEECH_BUBBLE_SIZE_SMALL,
  SPEECH_BUBBLE_TAIL_BOTTOM_END,
  SPEECH_BUBBLE_TAIL_BOTTOM_START,
  SPEECH_BUBBLE_TAIL_TOP,
  type SpeechBubbleSize,
  type SpeechBubbleTail,
} from './SpeechBubble.constants';

const TAIL_PX = 12;
const CORNER_TAIL_PX = 14;
const ROUND_PX = 18;
const SHARP_PX = 4;

const sideTail = (tail: SpeechBubbleTail) =>
  tail === SPEECH_BUBBLE_TAIL_TOP
    ? css`
        top: -${TAIL_PX / 2}px;
        left: calc(50% - ${TAIL_PX / 2}px);
      `
    : css`
        top: calc(50% - ${TAIL_PX / 2}px);
        left: -${TAIL_PX / 2}px;
      `;

const cornerTail = (tail: SpeechBubbleTail) =>
  tail === SPEECH_BUBBLE_TAIL_BOTTOM_START
    ? css`
        left: -6px;
        clip-path: polygon(100% 0, 100% 100%, 0 100%);
      `
    : css`
        right: -6px;
        clip-path: polygon(0 0, 100% 100%, 0 100%);
      `;

const isCorner = (tail: SpeechBubbleTail) =>
  tail === SPEECH_BUBBLE_TAIL_BOTTOM_START || tail === SPEECH_BUBBLE_TAIL_BOTTOM_END;

const radiusOf = (tail: SpeechBubbleTail, round: string) => {
  if (tail === SPEECH_BUBBLE_TAIL_BOTTOM_START) return `${round} ${round} ${round} ${SHARP_PX}px`;
  if (tail === SPEECH_BUBBLE_TAIL_BOTTOM_END) return `${round} ${round} ${SHARP_PX}px ${round}`;
  return round;
};

export const Root = styled.div<{ $tail: SpeechBubbleTail; $size: SpeechBubbleSize; $closable: boolean }>`
  position: relative;
  box-sizing: border-box;
  max-width: 100%;
  font-weight: 500;
  overflow-wrap: anywhere;

  ${({ $size, $tail, $closable, theme: { colors = {}, borderRadius = {} } }) => {
    const small = $size === SPEECH_BUBBLE_SIZE_SMALL;
    const round = small || isCorner($tail) ? `${ROUND_PX}px` : (borderRadius.extralarge ?? '28px');
    return css`
      padding: ${small ? '10px 16px' : '12px 16px'};
      ${$closable && 'padding-right: 34px;'}
      font-size: ${small ? '13px' : '14px'};
      line-height: ${small ? '18px' : '20px'};
      border-radius: ${radiusOf($tail, round)};
      background: ${colors.badge ?? '#7370B0'};
      color: ${colors.onBadge ?? '#FFFFFF'};
    `;
  }}

  &::before {
    content: '';
    position: absolute;
    background: inherit;

    ${({ $tail }) =>
      isCorner($tail)
        ? css`
            bottom: 0;
            width: ${CORNER_TAIL_PX}px;
            height: ${CORNER_TAIL_PX}px;
            ${cornerTail($tail)}
          `
        : css`
            width: ${TAIL_PX}px;
            height: ${TAIL_PX}px;
            transform: rotate(45deg);
            ${sideTail($tail)}
          `}
  }
`;

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
  30% { transform: translateY(-3px); opacity: 1; }
`;

export const Dots = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  height: 18px;
`;

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: ${bounce} 1.1s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.8;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: transparent;
  color: inherit;

  &:hover {
    background: color-mix(in srgb, currentColor 15%, transparent);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
  }
`;
